import { MASCOT_PALETTE } from "@/components/Others/MascotPanel";
import type { SoundName } from "@/lib/sound";
import { colors } from "@/theme";
import { DailySpinHistoryItem, DailySpinSegment } from "@/types";
import { formatBalance } from "@/utils";

/** Keyed on the session too, so signing in or out fetches the player's own standing. */
export const dailySpinStatusKey = (isLoggedIn: boolean) => ["daily-spin-status", isLoggedIn];

/** Slice colors for a wheel whose admin set none: the mascot's own, in turn. */
export const SEGMENT_PALETTE = [
  colors.prime,
  MASCOT_PALETTE.crown,
  MASCOT_PALETTE.sky,
  MASCOT_PALETTE.deep,
  "#0B63D6",
  "#A9E2FF",
  "#F7931A",
  "#36FF00",
];

export const segmentColor = (color: string, index: number): string =>
  color || SEGMENT_PALETTE[index % SEGMENT_PALETTE.length];

/** Full turns before the wheel settles, and how long that takes. */
export const SPIN_TURNS = 6;
export const SPIN_DURATION_MS = 5200;
export const REDUCED_SPIN_DURATION_MS = 500;

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** "$5.00 cash", "50 VIP points", "No prize": what a spin paid, in words. */
export const prizeLabel = (
  t: Translate,
  item: Pick<DailySpinHistoryItem, "prizeType" | "amount" | "currency">,
): string => {
  switch (item.prizeType) {
    case "cash":
      return t("daily-spin.prize-cash", { amount: formatBalance(item.amount, item.currency) });
    case "bonus":
      // "SKY" names the prize by itself, in every language.
      if (item.currency === "SKY") return `${item.amount.toLocaleString()} SKY`;
      return t("daily-spin.prize-bonus", { amount: formatBalance(item.amount, item.currency) });
    case "points":
      return t("daily-spin.prize-points", { count: item.amount.toLocaleString() });
    default:
      return t("daily-spin.prize-none");
  }
};

/**
 * The verdict the wheel sounds once it has stopped.
 *
 * The same three endings the casino games have: a blank falls, a prize rings,
 * and the fanfare is kept for the best prize of its kind on the wheel - when
 * there is more than one, so a wheel with a single cash slice does not play it
 * on every cash win.
 */
export const outcomeVoice = (
  prize: Pick<DailySpinHistoryItem, "prizeType" | "amount">,
  segments: DailySpinSegment[],
): SoundName => {
  if (prize.prizeType === "none") return "lose";
  const amounts = segments.filter((s) => s.type === prize.prizeType).map((s) => s.amount);
  if (amounts.length < 2) return "win";
  return prize.amount >= Math.max(...amounts) ? "bigWin" : "win";
};
